<template>
  <div
    class="q-mb-xl"
    :class="fullWidth ? 'full-width' : 'container'"
  >
    <div
      class="row q-col-gutter-md q-mb-md"
    >
      <div
        v-if="currentCircuit.isMultisite"
        class="text-subtitle2 text-uppercase text-left col-4"
      >
        Cinema
      </div>
      <div
        class="text-subtitle2 text-uppercase text-left"
        :class="currentCircuit.isMultisite ? 'col-3' : 'col-6'"
      >
        Subscription
      </div>
      <div
        v-if="enabledChannels.email"
        class="text-subtitle2 text-uppercase col"
      >
        Email
      </div>
      <div
        v-if="enabledChannels.push"
        class="text-subtitle2 text-uppercase col"
      >
        Push
      </div>
      <div
        v-if="enabledChannels.sms"
        class="text-subtitle2 text-uppercase col"
      >
        SMS
      </div>
      <div
        v-if="isAdmin"
        class="text-subtitle2 text-uppercase col"
      >
        {{ $t('actions.remove') }}
      </div>
    </div>
    <div
      v-for="({ marketingSubscription, userMarketingSubscription }) in activeSubscriptions"
      :key="userMarketingSubscription.id"
      class="row q-col-gutter-md q-mb-sm items-center"
    >
      <div
        v-if="currentCircuit.isMultisite"
        class="text-left col-4"
      >
        {{ marketingSubscription.siteName }}
      </div>
      <div
        class="text-left"
        :class="currentCircuit.isMultisite ? 'col-3' : 'col-6'"
      >
        {{ marketingSubscription.name }}
      </div>
      <div
        v-if="enabledChannels.email"
        class="col"
      >
        <q-checkbox
          v-if="marketingSubscription.hasChannelEnabled('email')"
          v-model="userMarketingSubscription.allowEmail"
          bottom-slots
          @click="updateMarketingPreferences(marketingSubscription, userMarketingSubscription)"
        />
      </div>
      <div
        v-if="enabledChannels.push"
        class="col"
      >
        <q-checkbox
          v-if="marketingSubscription.hasChannelEnabled('push')"
          v-model="userMarketingSubscription.allowPush"
          bottom-slots
        />
      </div>
      <div
        v-if="enabledChannels.sms"
        class="col"
      >
        <q-checkbox
          v-if="marketingSubscription.hasChannelEnabled('sms')"
          v-model="userMarketingSubscription.allowSms"
          bottom-slots
          @click="updateMarketingPreferences(marketingSubscription, userMarketingSubscription)"
        />
      </div>
      <div
        v-if="isAdmin"
        class="col"
      >
        <q-icon
          v-if="showDelete(marketingSubscription)"
          name="delete_forever"
          class="cursor-pointer"
          size="md"
          color="negative"
          :disabled="!canDelete(marketingSubscription)"
          @click="removeUserMarketingSubscriptions(marketingSubscription)"
        >
          <q-tooltip>
            {{ tooltipMessage(marketingSubscription) }}
          </q-tooltip>
        </q-icon>
      </div>
    </div>
  </div>
</template>

<script>
import User from 'src/models/user/User'
export default {
  name: 'UserMarketingSubscriptions',
  configuration: ['activeMarketingSubscriptions', 'currentSite', 'currentCircuit'],
  context: ['currentUser'],
  props: {
    user: {
      type: User,
      required: true
    },
    fullWidth: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  computed: {
    enabledChannels () {
      const allChannels = ['email', 'push', 'sms']
      const enabledChannels = {}

      allChannels.forEach(channel => {
        if (_.some(this.activeMarketingSubscriptions.data, (marketingSubscription) => {
          return marketingSubscription[`channel${_.capitalize(channel)}`]
        })) {
          enabledChannels[channel] = true
        }
      })

      return enabledChannels
    },
    activeSubscriptions () {
      return _.compact(_.map(this.activeMarketingSubscriptions.data, (marketingSubscription) => {
        const userMarketingSubscription = this.user.getUserMarketingSubscription(marketingSubscription)
        return userMarketingSubscription ? { marketingSubscription, userMarketingSubscription } : null
      }))
    },
    hasDirtySite () {
      return this.user.isDirty && _.includes(_.keys(this.user.describeDirty), 'siteId')
    },
    isAdmin () {
      return this.currentUser?.admin || false
    }
  },
  methods: {
    updateMarketingPreferences (marketingSubscription, userMarketingSubscription) {
      if (this.user.siteId !== marketingSubscription.siteId) {
        return
      }
      const name = marketingSubscription.name
      const allowEmail = userMarketingSubscription.allowEmail
      const allowSms = userMarketingSubscription.allowSms

      switch (name) {
        case 'Newsletter':
          this.user.emailNewsletter = allowEmail
          break
        case 'Showtimes':
          this.user.emailShowtimes = allowEmail
          this.user.textShowtimes = allowSms
          break
        case 'Last Chance':
          this.user.emailLastChance = allowEmail
          this.user.textLastChance = allowSms
          break
        case 'Promotions':
          this.user.emailPromotions = allowEmail
          break
        default:
          break
      }
    },
    removeUserMarketingSubscriptions (marketingSubscription) {
      if (!this.canDelete(marketingSubscription)) {
        return
      }

      const activeForSite = _.filter(this.activeSubscriptions, active => active.marketingSubscription.siteId === marketingSubscription.siteId)
      activeForSite.forEach(active => {
        this.user.userMarketingSubscriptions.remove(active.userMarketingSubscription)
      })
    },
    showDelete (marketingSubscription) {
      const currentIndex = _.findIndex(this.activeSubscriptions, active => active.marketingSubscription.id === marketingSubscription.id)
      const previousIndex = currentIndex > 0 ? currentIndex - 1 : null
      const previousMarketingSubscription = this.activeSubscriptions[previousIndex]?.marketingSubscription
      return !previousMarketingSubscription || previousMarketingSubscription.siteId !== marketingSubscription.siteId
    },
    canDelete (marketingSubscription) {
      if (this.hasDirtySite) {
        return false
      }

      return this.user.siteId !== marketingSubscription.siteId
    },
    tooltipMessage (marketingSubscription) {
      if (this.hasDirtySite) {
        return 'Please save your changes to continue'
      }

      const canDeleteMessage = this.canDelete(marketingSubscription) ? 'Remove all user marketing subscriptions for unlinked site' : 'Unable to remove user marketing subscriptions for linked site'
      return `${canDeleteMessage} ${marketingSubscription.siteName}`
    }
  }

}
</script>

<style scoped>
.container {
  width: 100%;
  max-width: 500px;
}
</style>
